<template>
  <span
    class="d-label"
    :class="classes"
  >
    <slot />
  </span>
</template>

<script>
import {
  WHITE,
  GRAY,
  PRIMARY,
  SECONDARY,
  ALERT,
  STAR,
  PINK,
  BUSINESS,
  ORANGE
} from '~/constants/colors'

/** 指定可能な色リスト */
export const LABEL_COLORS = Object.freeze([
  WHITE,
  ...GRAY,
  ...PRIMARY,
  ...SECONDARY,
  ...ALERT,
  STAR,
  PINK,
  ...BUSINESS,
  ...ORANGE
])

/** 角 */
export const CORNERS = Object.freeze(['TopLeft', 'TopRight', 'BottomLeft', 'BottomRight'])

/** @type {(value: string) => boolean} */
const colorValidator = val => {
  return val === null || val === '' || LABEL_COLORS.includes(val)
}

/** @type {import('vue').ComponentOptions} */
const dLabel = {
  name: 'DLabel',
  props: {
    /** テキスト色 */
    textColor: {
      type: String,
      default: 'Secondary',
      validator: colorValidator
    },
    /** 枠線色 */
    borderColor: {
      type: String,
      default: null,
      validator: colorValidator
    },
    /** 背景色 */
    backgroundColor: {
      type: String,
      default: null,
      validator: colorValidator
    },
    disabledRoundedCorners: {
      type: Array,
      default: () => [],
      validator(val) {
        if (!Array.isArray(val)) {
          return false
        }
        return val.every(x => CORNERS.includes(x))
      }
    },
    rounded: {
      type: Boolean,
      default: false
    },
    hasDivider: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    classes() {
      const classNamePrefix = 'd-label'
      const classes = []
      if (this.textColor) {
        classes.push(`${classNamePrefix}-text${this.textColor}`)
      }
      if (this.borderColor) {
        classes.push(`${classNamePrefix}-hasBorder`)
        classes.push(`${classNamePrefix}-border${this.borderColor}`)
      }
      if (this.backgroundColor) {
        classes.push(`${classNamePrefix}-background${this.backgroundColor}`)
      }
      classes.push(
        ...this.disabledRoundedCorners.map(corner => `${classNamePrefix}-disabledRounded${corner}`)
      )
      if (this.rounded) {
        classes.push(`${classNamePrefix}-rounded`)
      }
      if (this.hasDivider) {
        classes.push(`${classNamePrefix}-divider`)
      }
      return classes
    }
  }
}
export default dLabel
</script>

<style lang="scss" scoped>
.d-label {
  // テキスト色
  @include ds2-colors('white', 'text');
  @include ds2-colors('gray', 'text');
  @include ds2-colors('primary', 'text');
  @include ds2-colors('secondary', 'text');
  @include ds2-colors('alert', 'text');
  @include ds2-colors('orange', 'text');
  @include ds2-colors('star', 'text');
  @include ds2-colors('pink', 'text');
  @include ds2-colors('business', 'text');
  // 枠線色
  @include ds2-colors('gray', 'border');
  @include ds2-colors('primary', 'border');
  @include ds2-colors('secondary', 'border');
  @include ds2-colors('alert', 'border');
  @include ds2-colors('orange', 'border');
  @include ds2-colors('star', 'border');
  @include ds2-colors('pink', 'border');
  @include ds2-colors('business', 'border');
  // 背景色
  @include ds2-colors('white', 'background');
  @include ds2-colors('gray', 'background');
  @include ds2-colors('primary', 'background');
  @include ds2-colors('secondary', 'background');
  @include ds2-colors('alert', 'background');
  @include ds2-colors('orange', 'background');
  @include ds2-colors('star', 'background');
  @include ds2-colors('pink', 'background');
  @include ds2-colors('business', 'text');

  display: inline-block;
  padding: 3px 8px;
  border: 0;
  border-radius: 2px;
  font-size: 11px;
  line-height: 1.2;
  &-hasBorder {
    padding: 2px 7px;
    border-width: 1px;
    border-style: solid;
  }
  &-rounded {
    border-radius: 10px;
  }
  &-divider {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    &::before {
      margin-right: 4px;
      content: '|';
      font-size: 14px;
    }
  }

  &-disabledRoundedTopLeft {
    border-top-left-radius: 0;
  }
  &-disabledRoundedTopRight {
    border-top-right-radius: 0;
  }
  &-disabledRoundedBottomLeft {
    border-bottom-left-radius: 0;
  }
  &-disabledRoundedBottomRight {
    border-bottom-right-radius: 0;
  }
}
</style>
